import PrinterTransports from '../../constants/PrinterTransports'
import LabelPrinter, { isLabelPrinterAvailable } from '../print/labelPrinter'
import PrinterStorage from '../storage/printer-storage/PrinterStorage'

/**
 * Order matters — the drawer row cycles through this list on each tap.
 */
export const getAvailableTransports = (): PrinterTransports[] => [
  PrinterTransports.AUTO,
  PrinterTransports.BLUETOOTH,
  PrinterTransports.USB,
]

const isSupportedTransport = (value: string | undefined): value is PrinterTransports =>
  getAvailableTransports().some(transport => transport === value)

/**
 * Read per print rather than cached: the seller can change this from the drawer at any
 * time, and the repository that consumes it is a module-scope singleton.
 */
export const getPreferredTransport = (): PrinterTransports => {
  const storedTransport = PrinterStorage.getTransport()

  return isSupportedTransport(storedTransport) ? storedTransport : PrinterTransports.AUTO
}

export const setPreferredTransport = (transport: PrinterTransports): void =>
  PrinterStorage.setTransport(transport)

export const getPreferredBluetoothName = (): string | undefined => PrinterStorage.getBluetoothName()

export const setPreferredBluetoothName = (name: string): void => PrinterStorage.setBluetoothName(name)

/**
 * 203 dpi (8 dots/mm) is the default because it is by far the most common desktop
 * label printer resolution, including the ZD230 in use. It is only a fallback — the
 * real value is detected from the printer by identifyPrinter() and stored.
 */
export const DEFAULT_PRINTER_DPI = 203

export const getPrinterDpi = (): number => PrinterStorage.getDpi() ?? DEFAULT_PRINTER_DPI

export const setPrinterDpi = (dpi: number): void => PrinterStorage.setDpi(dpi)

export const getPrinterModel = (): string | undefined => PrinterStorage.getModel()

export const setPrinterModel = (model: string): void => PrinterStorage.setModel(model)

export const getPreferredUsbDeviceKey = (): string | undefined => PrinterStorage.getUsbDeviceKey()

export const setPreferredUsbDeviceKey = (deviceKey: string): void =>
  PrinterStorage.setUsbDeviceKey(deviceKey)

/**
 * Resolves AUTO to a concrete transport at print time.
 *
 * A cable wins over Bluetooth when one is actually usable, because a seller who has
 * just plugged a printer in has expressed a clearer intent than a pairing that may
 * have been set up months ago. "Usable" means present *and* already permitted —
 * an unpermitted device is skipped rather than interrupting a print with a system
 * dialog the seller did not ask for; they grant it once from the drawer instead.
 */
export const resolveTransport = async (): Promise<PrinterTransports.BLUETOOTH | PrinterTransports.USB> => {
  const preferredTransport = getPreferredTransport()

  if (preferredTransport !== PrinterTransports.AUTO) {
    return preferredTransport
  }

  if (!isLabelPrinterAvailable()) {
    return PrinterTransports.BLUETOOTH
  }

  try {
    const { devices } = await LabelPrinter.listUsbDevices()

    // `supported` matters as much as `hasPermission`: listUsbDevices reports every
    // attached USB device so unrecognised printers stay diagnosable, and without this
    // filter an unrelated permitted peripheral would divert printing to a device with
    // nothing to write to.
    //
    // Absent counts as supported, and only an explicit false is rejected. This JS
    // ships over OTA while the plugin ships in the APK, so a tablet can run a build
    // whose plugin predates the field and omits it entirely — treating that as
    // unsupported would silently disable USB on exactly the devices already using it.
    if (devices.some(device => device.supported !== false && device.hasPermission)) {
      return PrinterTransports.USB
    }
  } catch (error) {
    // Enumeration failing is not a print failure — fall through to Bluetooth, which
    // is what every existing installation already used.
    console.warn('[printerSettings] could not enumerate USB printers, falling back to Bluetooth', error)
  }

  return PrinterTransports.BLUETOOTH
}
